<template>
  <div class="voter-list-admin">
    <!-- Search Input -->
    <div class="search-container">
      <div class="search-input-wrapper">
        <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
        </svg>
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="$t('Search voters...')"
          class="search-input"
        />
        <button
          v-if="searchQuery"
          @click="searchQuery = ''"
          class="clear-search"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
      <div class="voter-count">
        <span class="count-number">{{ filteredVotersCount }}</span>
        <span class="count-label">{{ filteredVotersCount === 1 ? $t('voter') : $t('voters') }}</span>
      </div>
    </div>

    <!-- Scrollable Voter List -->
    <div class="voter-scroll-container" ref="scrollContainer">
      <TransitionGroup name="voter-list" tag="ul" class="voter-list">
        <!-- User Voters -->
        <li
          v-for="(voter, index) in filteredUserVoters"
          :key="'user-' + voter.id"
          class="voter-item"
        >
          <div class="voter-content">
            <tippy
              interactive
              :animate-fill="false"
              placement="bottom-start"
              theme="light"
              animation="fade"
              trigger="mouseenter"
              v-if="voter.handle"
            >
              <template v-slot:trigger>
                <div class="voter-info">
                  <avatar
                    class="voter-avatar"
                    :class="{ 'voter-avatar--admin': isAdmin(voter.id) }"
                    :size="36"
                    :src="voter.avatar_url"
                    :username="voter.name"
                  />
                  <div class="voter-details">
                    <a :href="'/users/' + voter.handle" class="voter-name">{{ voter.name }}</a>
                    <span class="voter-type-badge user-badge">{{ $t('User') }}</span>
                  </div>
                </div>
              </template>
              <tooltip-tippy :user="voter"></tooltip-tippy>
            </tippy>
            <div v-else class="voter-info">
              <avatar
                class="voter-avatar"
                :class="{ 'voter-avatar--admin': isAdmin(voter.id) }"
                :size="36"
                :src="voter.avatar_url"
                :username="voter.name"
              />
              <div class="voter-details">
                <span class="voter-name-text">{{ voter.name }}</span>
                <span class="voter-type-badge user-badge">{{ $t('User') }}</span>
              </div>
            </div>
          </div>
          <button
            v-if="canDelete(voter)"
            @click="deleteUserVote(voter, index)"
            class="delete-btn"
            :title="$t('Remove this vote')"
            v-tippy="{ placement: 'top' }"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </li>

        <!-- Behalf Voters -->
        <li
          v-for="(voter, index) in filteredBehalfVoters"
          :key="'behalf-' + voter.id"
          class="voter-item"
        >
          <div class="voter-content">
            <div class="voter-info">
              <avatar
                class="voter-avatar"
                :size="36"
                :src="voter.avatar_url"
                :username="voter.name"
              />
              <div class="voter-details">
                <span class="voter-name-text">{{ voter.name }}</span>
                <span class="voter-type-badge behalf-badge">{{ $t('Added') }}</span>
              </div>
            </div>
          </div>
          <button
            v-if="canDelete(voter)"
            @click="deleteBehalfVote(voter, index)"
            class="delete-btn"
            :title="$t('Remove this vote')"
            v-tippy="{ placement: 'top' }"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </li>

        <!-- Post Voters -->
        <li
          v-for="(voter, index) in filteredPostVoters"
          :key="'post-voter-' + voter.id"
          class="voter-item"
        >
          <div class="voter-content">
            <tippy
              interactive
              :animate-fill="false"
              placement="bottom-start"
              theme="light"
              animation="fade"
              trigger="mouseenter"
              v-if="voter.handle"
            >
              <template v-slot:trigger>
                <div class="voter-info">
                  <avatar
                    class="voter-avatar"
                    :size="36"
                    :src="voter.avatar ? voter.avatar.url : ''"
                    :username="voter.name"
                  />
                  <div class="voter-details">
                    <a :href="'/users/' + voter.handle" class="voter-name">{{ voter.name }}</a>
                    <span class="voter-type-badge post-voter-badge">{{ $t('Contact') }}</span>
                  </div>
                </div>
              </template>
              <tooltip-tippy :user="voter"></tooltip-tippy>
            </tippy>
            <div v-else class="voter-info">
              <avatar
                class="voter-avatar"
                :size="36"
                :src="voter.avatar ? voter.avatar.url : ''"
                :username="voter.name"
              />
              <div class="voter-details">
                <span class="voter-name-text">{{ voter.name }}</span>
                <span class="voter-type-badge post-voter-badge">{{ $t('Contact') }}</span>
              </div>
            </div>
          </div>
          <button
            v-if="canDelete(voter)"
            @click="deletePostVoterVote(voter, index)"
            class="delete-btn"
            :title="$t('Remove this vote')"
            v-tippy="{ placement: 'top' }"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </li>

        <!-- Anonymous Voters -->
        <li
          v-for="(voter, index) in filteredAnonymousVoters"
          :key="'anon-' + voter.id"
          class="voter-item"
        >
          <div class="voter-content">
            <div class="voter-info">
              <div class="anonymous-avatar">
                <svg viewBox="0 0 258.75 258.75">
                  <circle fill="#fff" cx="129.375" cy="90" r="30"/>
                  <path fill="#fff" style="transform: scale(0.5) translate(130px, 100px);" d="M129.375,150c-60.061,0-108.75,48.689-108.75,108.75h217.5C238.125,198.689,189.436,150,129.375,150z"/>
                </svg>
              </div>
              <div class="voter-details">
                <span class="voter-name-text">{{ $t('Anonymous Voter') }}</span>
                <span class="voter-type-badge anonymous-badge">{{ $t('Anonymous') }}</span>
              </div>
            </div>
          </div>
          <button
            @click="deleteAnonymousVote(voter, index)"
            class="delete-btn"
            :title="$t('Remove this vote')"
            v-tippy="{ placement: 'top' }"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </li>
      </TransitionGroup>

      <!-- Empty State -->
      <div v-if="filteredVotersCount === 0" class="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
        <p v-if="searchQuery">{{ $t('No voters match "{query}"', { query: searchQuery }) }}</p>
        <p v-else>{{ $t('No voters yet') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import avatar from "vue-avatar";
import EventBus from "event_bus";

export default {
  name: "VoterListAdmin",
  components: { avatar },
  props: {
    userVoters: { type: Array, default: () => [] },
    behalfVoters: { type: Array, default: () => [] },
    postVoters: { type: Array, default: () => [] },
    anonymousVoters: { type: Array, default: () => [] },
    post: { type: Object, required: true },
    currentUser: { type: Object, default: () => ({}) },
    adminIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      searchQuery: "",
      localUserVoters: [...this.userVoters],
      localBehalfVoters: [...this.behalfVoters],
      localPostVoters: [...this.postVoters],
      localAnonymousVoters: [...this.anonymousVoters]
    };
  },
  mounted() {
    EventBus.$on("userVoter", (vote) => {
      this.localUserVoters.push(vote);
    });
    EventBus.$on("anonymousVoter", (vote) => {
      this.localBehalfVoters.push(vote);
    });
    EventBus.$on("postVoterVote", (vote) => {
      this.localPostVoters.push(vote);
    });
    EventBus.$on("currentUserVoted", (user) => {
      if (user && user.id && !this.localUserVoters.some((v) => v.id === user.id)) {
        this.localUserVoters.push(user);
      }
    });
    EventBus.$on("currentUserUnvoted", (userId) => {
      this.localUserVoters = this.localUserVoters.filter((v) => v.id !== userId);
    });
  },
  beforeDestroy() {
    EventBus.$off("userVoter");
    EventBus.$off("anonymousVoter");
    EventBus.$off("postVoterVote");
    EventBus.$off("currentUserVoted");
    EventBus.$off("currentUserUnvoted");
  },
  computed: {
    filteredUserVoters() {
      return this.filterVoters(this.localUserVoters);
    },
    filteredBehalfVoters() {
      return this.filterVoters(this.localBehalfVoters);
    },
    filteredPostVoters() {
      return this.filterVoters(this.localPostVoters);
    },
    filteredAnonymousVoters() {
      if (!this.searchQuery) return this.localAnonymousVoters;
      const query = this.searchQuery.toLowerCase();
      if ("anonymous".includes(query) || "anonymous voter".includes(query)) {
        return this.localAnonymousVoters;
      }
      return [];
    },
    filteredVotersCount() {
      return (
        this.filteredUserVoters.length +
        this.filteredBehalfVoters.length +
        this.filteredPostVoters.length +
        this.filteredAnonymousVoters.length
      );
    }
  },
  methods: {
    filterVoters(voters) {
      if (!this.searchQuery) return voters;
      const query = this.searchQuery.toLowerCase();
      return voters.filter(v =>
        (v.name && v.name.toLowerCase().includes(query)) ||
        (v.email && v.email.toLowerCase().includes(query))
      );
    },
    isAdmin(id) {
      return this.adminIds.includes(id);
    },
    canDelete(voter) {
      if (voter.email) {
        return this.currentUser.email !== voter.email;
      }
      return true;
    },
    getVoteUrl() {
      return `/boards/${this.post.board_id}/posts/${this.post.id}/vote_behalf_user`;
    },
    deleteUserVote(voter, index) {
      const data = new FormData();
      data.append("voterid", voter.id);
      if (voter.email) {
        if (voter.handle && !voter.behalf_user) {
          data.append("voting_mode", "delete_normal_user_vote");
        } else {
          data.append("voting_mode", "delete_user_vote");
        }
      } else {
        data.append("voting_mode", "delete_anonymous_vote");
      }
      this.performDelete(data, () => {
        const idx = this.localUserVoters.findIndex(v => v.id === voter.id);
        if (idx !== -1) this.localUserVoters.splice(idx, 1);
      });
    },
    deleteBehalfVote(voter, index) {
      const data = new FormData();
      data.append("voterid", voter.id);
      if (voter.email) {
        data.append("voting_mode", "delete_user_vote");
      } else {
        data.append("voting_mode", "delete_anonymous_vote");
      }
      this.performDelete(data, () => {
        const idx = this.localBehalfVoters.findIndex(v => v.id === voter.id);
        if (idx !== -1) this.localBehalfVoters.splice(idx, 1);
      });
    },
    deletePostVoterVote(voter, index) {
      const data = new FormData();
      data.append("voter_id", voter.id);
      data.append("voting_mode", "delete_post_voter_vote");
      this.performDelete(data, () => {
        const idx = this.localPostVoters.findIndex(v => v.id === voter.id);
        if (idx !== -1) this.localPostVoters.splice(idx, 1);
      });
    },
    deleteAnonymousVote(voter, index) {
      const data = new FormData();
      data.append("vote_id", voter.id);
      data.append("voting_mode", "delete_annonymous_vote");
      this.performDelete(data, () => {
        const idx = this.localAnonymousVoters.findIndex(v => v.id === voter.id);
        if (idx !== -1) this.localAnonymousVoters.splice(idx, 1);
      });
    },
    performDelete(data, onSuccess) {
      Rails.ajax({
        url: this.getVoteUrl(),
        type: "POST",
        data: data,
        beforeSend: () => true,
        success: (response) => {
          onSuccess();
          if (response.post) {
            EventBus.$emit("updateUpvotes", response.post);
          }
        },
        error: () => {
          console.error("Failed to delete vote");
          alert(this.$t("Failed to remove vote. Please try again."));
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.voter-list-admin {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.search-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  width: 16px;
  height: 16px;
  color: #a3a3a3;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 32px 8px 34px;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  transition: all 0.15s ease;
  outline: none;

  &::placeholder {
    color: #a3a3a3;
  }

  &:focus {
    border-color: var(--accent-color, #009be5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color, #009be5) 15%, transparent);
  }
}

.clear-search {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #a3a3a3;
  transition: color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: #737373;
  }
}

.voter-count {
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 10px;
  background: #f3f4f6;
  border-radius: 6px;
}

.count-number {
  font-size: 14px;
  font-weight: 600;
  color: #404040;
}

.count-label {
  font-size: 12px;
  color: #737373;
}

.voter-scroll-container {
  max-height: 280px;
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
}

.voter-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.voter-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
}

.voter-content {
  flex: 1;
  min-width: 0;
}

.voter-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.voter-avatar {
  flex-shrink: 0;
  border-radius: 8px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.voter-avatar--admin {
  border-color: var(--project-500, #6366f1);
}

.anonymous-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: #6b7280;
  border-radius: 8px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
}

.voter-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.voter-name {
  font-size: 13px;
  font-weight: 500;
  color: #404040;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.15s ease;

  &:hover {
    color: var(--project-500, #6366f1);
    text-decoration: none;
  }
}

.voter-name-text {
  font-size: 13px;
  font-weight: 500;
  color: #404040;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.voter-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 4px;
  width: fit-content;
}

.user-badge {
  background: #dbeafe;
  color: #1d4ed8;
}

.behalf-badge {
  background: #fef3c7;
  color: #b45309;
}

.post-voter-badge {
  background: #d1fae5;
  color: #047857;
}

.anonymous-badge {
  background: #e5e7eb;
  color: #5c5c5c;
}

.delete-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #d1d5db;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: #a3a3a3;

  svg {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 13px;
    text-align: center;
  }
}

// Transition animations
.voter-list-enter-active,
.voter-list-leave-active {
  transition: all 0.25s ease;
}

.voter-list-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.voter-list-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

.voter-list-move {
  transition: transform 0.25s ease;
}
</style>
